import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {AdBanner} from '../pages/ProtectedRoute';
import SEO from '../components/SEO';
/* ─── TOOL CATEGORIES ───────────────────────────────────────────────── */
const CATEGORIES = [
  {
    id:    'network',
    label: 'Network Tools',
    icon:  '🌐',
    color: '#00c8f0',
    glow:  'rgba(0,200,240,.18)',
    path:  '/tools/ip',
    desc:  'IP lookup, DNS, WHOIS, port scanner, traceroute, blacklist check, SSL checker and more',
    badge: '12 tools',
    tools: [
      { name:'What Is My IP',   icon:'📡' },
      { name:'IP Lookup',       icon:'🔍' },
      { name:'DNS Lookup',      icon:'🌍' },
      { name:'MX Record',       icon:'📬' },
      { name:'Port Scanner',    icon:'🔌' },
      { name:'Traceroute',      icon:'🗺️' },
      { name:'WHOIS',           icon:'📋' },
      { name:'SSL Checker',     icon:'🔒' },
      { name:'HTTP Headers',    icon:'📄' },
      { name:'Blacklist Check', icon:'🛡️' },
      { name:'Speed Test',      icon:'⚡' },
      { name:'SPF Record',      icon:'📧' },
    ],
  },
  {
    id:    'developer',
    label: 'Developer Tools',
    icon:  '⌨️',
    color: '#b78cff',
    glow:  'rgba(183,140,255,.18)',
    path:  '/tools/dev',
    desc:  'JSON, YAML, Base64, URL encoder, JWT decoder, UUID generator, hash, HMAC, regex tester',
    badge: '13 tools',
    tools: [
      { name:'JSON Formatter',  icon:'{ }' },
      { name:'JSON Validator',  icon:'✓'   },
      { name:'Text Diff',       icon:'±'   },
      { name:'YAML Formatter',  icon:'~'   },
      { name:'Base64',          icon:'b64' },
      { name:'URL Encoder',     icon:'🔗'  },
      { name:'JWT Decoder',     icon:'🔑'  },
      { name:'UUID Generator',  icon:'#'   },
      { name:'Hash Generator',  icon:'##'  },
      { name:'HMAC Generator',  icon:'🔐'  },
      { name:'Regex Tester',    icon:'.*'  },
    ],
  },
  {
    id:    'text',
    label: 'Text Tools',
    icon:  '📝',
    color: '#ff9f43',
    glow:  'rgba(255,159,67,.18)',
    path:  '/tools/text',
    desc:  'Word counter, case converter, remove duplicates, lorem ipsum, markdown preview, HTML encoder',
    badge: '7 tools',
    tools: [
      { name:'Word Counter',    icon:'📊' },
      { name:'Case Converter',  icon:'Aa' },
      { name:'Remove Duplicates',icon:'🔄'},
      { name:'Lorem Ipsum',     icon:'📄' },
      { name:'Markdown Preview',icon:'✍️' },
      { name:'HTML Encoder',    icon:'< >' },
      { name:'HTML → Markdown', icon:'↓'  },
    ],
  },
  {
    id:    'finance',
    label: 'Finance Tools',
    icon:  '💰',
    color: '#2ecc71',
    glow:  'rgba(46,204,113,.18)',
    path:  '/tools/finance',
    desc:  'FIRE calculator, mortgage, compound interest, RRSP, TFSA, dividend yield, currency converter',
    badge: '26 tools',
    tools: [
      { name:'FIRE Calculator',  icon:'🔥' },
      { name:'Coast FIRE',       icon:'🌊' },
      { name:'Compound Interest',icon:'📈' },
      { name:'Mortgage',         icon:'🏠' },
      { name:'Loan Payment',     icon:'📅' },
      { name:'RRSP',             icon:'🍁' },
      { name:'TFSA Growth',      icon:'💎' },
      { name:'SIP Calculator',   icon:'📆' },
      { name:'Dividend Yield',   icon:'💵' },
      { name:'Currency Converter',icon:'🌍'},
    ],
  },
  {
    id:    'business',
    label: 'Business Tools',
    icon:  '💼',
    color: '#1abc9c',
    glow:  'rgba(26,188,156,.18)',
    path:  '/tools/business',
    desc:  'Invoice generator, GST/VAT calculator, profit margin, markup, freelance rate, payroll',
    badge: '6 tools',
    tools: [
      { name:'Invoice Generator', icon:'🧾' },
      { name:'GST/VAT Calculator',icon:'🧮' },
      { name:'Profit Margin',     icon:'📈' },
      { name:'Markup Calculator', icon:'💹' },
      { name:'Freelance Rate',    icon:'⏱' },
      { name:'Payroll Calculator',icon:'💰' },
    ],
  },
  {
    id:    'files',
    label: 'File Tools',
    icon:  '🗂️',
    color: '#3dd68c',
    glow:  'rgba(61,214,140,.18)',
    path:  '/tools/files',
    desc:  'Convert images, merge PDFs, compress PDFs, resize images, QR code generator',
    badge: '6 tools',
    tools: [
      { name:'Image Converter',  icon:'🖼️' },
      { name:'Image Compressor', icon:'🗜️' },
      { name:'Image Resizer',    icon:'↔️' },
      { name:'PDF Merger',       icon:'📑' },
      { name:'PDF Compressor',   icon:'🗜️' },
      { name:'QR Code Generator',icon:'⬛' },
    ],
  },
];

/* ─── STATS ─────────────────────────────────────────────────────────── */
const STATS = [
  { value:'74+',  label:'Free tools'               },
  { value:'100%', label:'Browser-side where possible' },
  { value:'0',    label:'Sign-ups required'         },
  { value:'Almost Free', label:'Always'                    },
];

/* ─── FLOATING GRID BACKGROUND ──────────────────────────────────────── */
function GridBg() {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:0, pointerEvents:'none',
      overflow:'hidden',
    }}>
      {/* grid lines */}
      <div style={{
        position:'absolute', inset:0,
        backgroundImage:`
          linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)
        `,
        backgroundSize:'60px 60px',
      }}/>
      {/* radial gradient overlay */}
      <div style={{
        position:'absolute', inset:0,
        background:'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(0,200,240,.06) 0%, transparent 70%)',
      }}/>
      {/* corner glow */}
      <div style={{
        position:'absolute', top:-200, right:-200,
        width:600, height:600, borderRadius:'50%',
        background:'radial-gradient(circle, rgba(183,140,255,.06) 0%, transparent 70%)',
      }}/>
      <div style={{
        position:'absolute', bottom:-200, left:-200,
        width:500, height:500, borderRadius:'50%',
        background:'radial-gradient(circle, rgba(61,214,140,.05) 0%, transparent 70%)',
      }}/>
    </div>
  );
}

/* ─── TOOL CHIP ──────────────────────────────────────────────────────── */
function ToolChip({ icon, name, color }) {
  return (
    <div style={{
      display:'inline-flex', alignItems:'center', gap:5,
      padding:'4px 9px',
      borderRadius:6,
      background:`${color}10`,
      border:`1px solid ${color}22`,
      fontSize:11.5,
      color:`${color}cc`,
      whiteSpace:'nowrap',
    }}>
      <span style={{ fontSize:11 }}>{icon}</span>
      {name}
    </div>
  );
}

/* ─── CATEGORY CARD ──────────────────────────────────────────────────── */
function CategoryCard({ cat, index }) {
  const [hovered, setHovered] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), index * 120);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <Link
      to={cat.path}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        textDecoration:'none',
        display:'block',
        opacity:   mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(24px)',
        transition:'opacity .5s ease, transform .5s ease',
      }}
    >
      <div style={{
        position:'relative',
        background: hovered
          ? `linear-gradient(135deg, rgba(255,255,255,.04), rgba(255,255,255,.02))`
          : 'rgba(255,255,255,.025)',
        border:`1px solid ${hovered ? cat.color+'45' : 'rgba(255,255,255,.07)'}`,
        borderRadius:16,
        padding:'28px 26px',
        overflow:'hidden',
        transition:'all .22s ease',
        cursor:'pointer',
        boxShadow: hovered ? `0 8px 40px ${cat.glow}, 0 0 0 1px ${cat.color}20` : 'none',
      }}>

        {/* top glow on hover */}
        <div style={{
          position:'absolute', top:0, left:0, right:0, height:1,
          background: hovered
            ? `linear-gradient(90deg, transparent, ${cat.color}60, transparent)`
            : 'transparent',
          transition:'all .22s',
        }}/>

        {/* corner accent */}
        <div style={{
          position:'absolute', top:-30, right:-30,
          width:120, height:120, borderRadius:'50%',
          background:`radial-gradient(circle, ${cat.color}${hovered?'12':'08'} 0%, transparent 70%)`,
          transition:'all .22s',
        }}/>

        {/* header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{
              width:46, height:46, borderRadius:12,
              background:`${cat.color}15`,
              border:`1px solid ${cat.color}30`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:22,
              boxShadow: hovered ? `0 0 20px ${cat.color}25` : 'none',
              transition:'box-shadow .22s',
            }}>{cat.icon}</div>
            <div>
              <div style={{
                fontSize:18, fontWeight:800,
                color:'rgba(221,229,245,.95)',
                fontFamily:'"Syne","DM Sans",system-ui,sans-serif',
                letterSpacing:'-.01em',
              }}>{cat.label}</div>
              <div style={{
                fontSize:11, fontWeight:600,
                color:`${cat.color}`,
                letterSpacing:'.08em', textTransform:'uppercase',
                marginTop:1,
              }}>{cat.badge}</div>
            </div>
          </div>

          {/* arrow */}
          <div style={{
            width:30, height:30, borderRadius:8,
            background:`${cat.color}12`,
            border:`1px solid ${cat.color}25`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:13, color:cat.color,
            transform: hovered ? 'translate(2px,-2px)' : 'none',
            transition:'transform .18s',
          }}>→</div>
        </div>

        {/* description */}
        <p style={{
          fontSize:13, color:'rgba(221,229,245,.5)',
          lineHeight:1.65, marginBottom:18,
          fontFamily:'"DM Sans",system-ui,sans-serif',
        }}>{cat.desc}</p>

        {/* tool chips */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
          {cat.tools.map(t => (
            <ToolChip key={t.name} icon={t.icon} name={t.name} color={cat.color}/>
          ))}
        </div>
      </div>
    </Link>
  );
}

/* ─── STAT ITEM ──────────────────────────────────────────────────────── */
function StatItem({ value, label, index }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 300 + index * 80);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div style={{
      textAlign:'center',
      opacity:   mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(12px)',
      transition:'opacity .45s ease, transform .45s ease',
    }}>
      <div style={{
        fontSize:'clamp(1.8rem,3.5vw,2.4rem)',
        fontWeight:900,
        background:'linear-gradient(135deg, #fff 30%, rgba(0,200,240,.8))',
        WebkitBackgroundClip:'text',
        WebkitTextFillColor:'transparent',
        backgroundClip:'text',
        fontFamily:'"Syne",system-ui,sans-serif',
        letterSpacing:'-.03em',
        lineHeight:1,
        marginBottom:6,
      }}>{value}</div>
      <div style={{
        fontSize:12,
        color:'rgba(221,229,245,.38)',
        fontFamily:'"DM Sans",system-ui,sans-serif',
        letterSpacing:'.02em',
      }}>{label}</div>
    </div>
  );
}

const toolsLandingSchema = {
  "@context":    "https://schema.org",
  "@type":       "WebSite",
  "name":        "Tylented Tools",
  "url":         "https://arhat.info/tools",
  "description": "74+ free everyday tools — FIRE calculators, mortgage tools, invoice generator, business calculators, text tools, file converters, network utilities.",
  "publisher": {
    "@type": "Organization",
    "name":  "Tylented",
    "url":   "https://arhat.info",
    "logo":  "https://arhat.info/logo.png"
  },
  "hasPart": [
    { "@type":"WebApplication","name":"Finance & FIRE Tools", "url":"https://arhat.info/tools/finance"  },
    { "@type":"WebApplication","name":"Business Tools",       "url":"https://arhat.info/tools/business" },
    { "@type":"WebApplication","name":"Network Tools",        "url":"https://arhat.info/tools/ip"       },
    { "@type":"WebApplication","name":"Developer Tools",      "url":"https://arhat.info/tools/dev"      },
    { "@type":"WebApplication","name":"Text Tools",           "url":"https://arhat.info/tools/text"     },
    { "@type":"WebApplication","name":"File Tools",           "url":"https://arhat.info/tools/files"    },
  ]
};
/* ─── MAIN PAGE ──────────────────────────────────────────────────────── */
export default function ToolsLanding() {
  const [headingMounted, setHeadingMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeadingMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        a { text-decoration:none; }
      `}</style>

      <div style={{
        minHeight:'100vh',
        background:'#07080c',
        fontFamily:'"DM Sans",system-ui,sans-serif',
        position:'relative',
        overflow:'hidden',
      }}>

<SEO
  title="Free Online Tools — Finance, FIRE, Business, Text & More | Tylented"
  description="74+ free online tools for everyday use: FIRE calculator, mortgage calculator, invoice generator, word counter, IP lookup, JSON formatter, PDF merger and more. No sign-up. No ads. Works in your browser."
  keywords="free online tools, FIRE calculator, mortgage calculator, invoice generator free, word counter, compound interest calculator, GST calculator, IP lookup, json formatter, pdf merger, everyday tools online, finance tools, business tools"
  canonical="https://arhat.info/tools"
  og={{
    title:       "74+ Free Tools — Finance, FIRE, Business & More | Tylented",
    description: "FIRE calculators, mortgage tools, invoice generator, word counter, IP lookup, JSON formatter and more. All free, no sign-up, works in your browser.",
    url:         "https://arhat.info/tools",
    image:       "https://arhat.info/og-tools.png",
  }}
/>
 
        <GridBg/>

        <div style={{ position:'relative', zIndex:1, padding:'0 0 64px' }}>

{/* <AdBanner slot="tools-sidebar" style={{ marginBottom:16 }}/> */}

          {/* ── HERO ───────────────────────────────────────────────── */}
          <div style={{
            textAlign:'center',
            padding:'72px 24px 56px',
            maxWidth:680,
            margin:'0 auto',
          }}>
                    <AdBanner slot="tools-top"/>

            {/* eyebrow */}
            <div style={{
              display:'inline-flex', alignItems:'center', gap:7,
              padding:'5px 14px',
              borderRadius:20,
              background:'rgba(0,200,240,.08)',
              border:'1px solid rgba(0,200,240,.2)',
              fontSize:11.5, fontWeight:700,
              color:'rgba(0,200,240,.9)',
              letterSpacing:'.1em', textTransform:'uppercase',
              marginBottom:24,
              opacity:   headingMounted ? 1 : 0,
              transform: headingMounted ? 'translateY(0)' : 'translateY(-10px)',
              transition:'opacity .5s ease, transform .5s ease',
            }}>
              <span style={{
                width:6, height:6, borderRadius:'50%',
                background:'#00c8f0',
                boxShadow:'0 0 8px #00c8f0',
                display:'inline-block',
              }}/>
              74+ Free Tools by Tylented
            </div>

            {/* heading */}
            <h1 style={{
              fontSize:'clamp(2.2rem,5.5vw,3.6rem)',
              fontWeight:900,
              lineHeight:1.08,
              letterSpacing:'-.03em',
              fontFamily:'"Syne",system-ui,sans-serif',
              marginBottom:18,
              opacity:   headingMounted ? 1 : 0,
              transform: headingMounted ? 'translateY(0)' : 'translateY(16px)',
              transition:'opacity .55s ease .08s, transform .55s ease .08s',
            }}>
              <span style={{ color:'rgba(221,229,245,.95)' }}>
                74+ free tools
              </span>
              <br/>
              <span style={{
                background:'linear-gradient(90deg, #00c8f0, #b78cff 50%, #3dd68c)',
                WebkitBackgroundClip:'text',
                WebkitTextFillColor:'transparent',
                backgroundClip:'text',
              }}>
                built for developers
              </span>
            </h1>

            {/* subheading */}
            <p style={{
              fontSize:'clamp(14px,2vw,16px)',
              color:'rgba(221,229,245,.48)',
              lineHeight:1.75,
              maxWidth:540,
              margin:'0 auto 32px',
              opacity:   headingMounted ? 1 : 0,
              transform: headingMounted ? 'translateY(0)' : 'translateY(12px)',
              transition:'opacity .55s ease .16s, transform .55s ease .16s',
            }}>
              Network diagnostics, developer utilities, finance calculators, text tools,
              file processing and business tools. No sign-up. No ads. No nonsense.
            </p>

            {/* CTA */}
            <div style={{
              display:'flex', justifyContent:'center', gap:10, flexWrap:'wrap',
              opacity:   headingMounted ? 1 : 0,
              transform: headingMounted ? 'translateY(0)' : 'translateY(10px)',
              transition:'opacity .55s ease .24s, transform .55s ease .24s',
            }}>
              {[
                { to:'/tools/ip',       label:'🌐 Network',   bg:'rgba(0,200,240,.12)',   border:'rgba(0,200,240,.35)',   color:'#00c8f0'  },
                { to:'/tools/dev',      label:'⌨️ Developer',  bg:'rgba(183,140,255,.1)',  border:'rgba(183,140,255,.3)',  color:'#b78cff'  },
                { to:'/tools/text',     label:'📝 Text',       bg:'rgba(255,159,67,.1)',   border:'rgba(255,159,67,.3)',   color:'#ff9f43'  },
                { to:'/tools/finance',  label:'💰 Finance',    bg:'rgba(46,204,113,.09)',  border:'rgba(46,204,113,.28)',  color:'#2ecc71'  },
                { to:'/tools/business', label:'💼 Business',   bg:'rgba(26,188,156,.09)',  border:'rgba(26,188,156,.28)',  color:'#1abc9c'  },
                { to:'/tools/files',    label:'🗂️ Files',      bg:'rgba(61,214,140,.09)',  border:'rgba(61,214,140,.28)',  color:'#3dd68c'  },
              ].map(btn => (
                <Link key={btn.to} to={btn.to} style={{
                  padding:'9px 18px', borderRadius:9,
                  background:btn.bg, border:`1px solid ${btn.border}`,
                  color:btn.color, fontSize:13, fontWeight:700,
                  fontFamily:'"Syne",system-ui,sans-serif',
                  display:'inline-flex', alignItems:'center', gap:6,
                  transition:'all .16s', textDecoration:'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity='.75'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity='1'; }}
                >
                  {btn.label}
                </Link>
              ))}
            </div>
          </div>

          {/* ── STATS ROW ──────────────────────────────────────────── */}
          <div style={{
            maxWidth:700,
            margin:'0 auto 56px',
            padding:'0 24px',
            display:'grid',
            gridTemplateColumns:'repeat(4,1fr)',
            gap:16,
          }}>
            {/* dividers */}
            <div style={{
              gridColumn:'1/-1',
              height:1,
              background:'linear-gradient(90deg, transparent, rgba(255,255,255,.07), transparent)',
              marginBottom:8,
            }}/>
            {STATS.map((s, i) => (
              <StatItem key={s.label} {...s} index={i}/>
            ))}
            <div style={{
              gridColumn:'1/-1',
              height:1,
              background:'linear-gradient(90deg, transparent, rgba(255,255,255,.07), transparent)',
              marginTop:8,
            }}/>
          </div>

          {/* ── CARDS GRID ─────────────────────────────────────────── */}
          <div style={{
            maxWidth:1100,
            margin:'0 auto',
            padding:'0 24px',
            display:'grid',
            gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))',
            gap:16,
          }}>
            {CATEGORIES.map((cat, i) => (
              <CategoryCard key={cat.id} cat={cat} index={i}/>
            ))}
          </div>

          {/* ── BOTTOM BANNER ──────────────────────────────────────── */}
          <div style={{
            maxWidth:1100,
            margin:'40px auto 0',
            padding:'0 24px',
          }}>
            <div style={{
              padding:'24px 28px',
              borderRadius:14,
              background:'rgba(255,255,255,.02)',
              border:'1px solid rgba(255,255,255,.06)',
              display:'flex',
              alignItems:'center',
              justifyContent:'space-between',
              flexWrap:'wrap',
              gap:16,
            }}>
              <div>
                <div style={{
                  fontSize:15, fontWeight:700,
                  color:'rgba(221,229,245,.85)',
                  fontFamily:'"Syne",system-ui,sans-serif',
                  marginBottom:4,
                }}>
                  Need a custom tool or software for your business?
                </div>
                <div style={{ fontSize:13, color:'rgba(221,229,245,.38)' }}>
                  We build backend systems, AI automation, and cloud infrastructure.
                  All 74+ tools are free — no account needed.
                </div>
              </div>
              <Link to="/contact" style={{
                padding:'10px 22px',
                borderRadius:9,
                background:'rgba(0,200,240,.1)',
                border:'1px solid rgba(0,200,240,.3)',
                color:'#00c8f0',
                fontSize:13, fontWeight:700,
                fontFamily:'"Syne",system-ui,sans-serif',
                whiteSpace:'nowrap',
                transition:'all .16s',
              }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(0,200,240,.18)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(0,200,240,.1)'; }}
              >
                Talk to us →
              </Link>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}